import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSData, DSSetup, RecordData, Service } from 'webapp-types';
import type {
	Aggregation,
	CompareFunction,
	SeriesVisualization,
	ShownLabels,
} from 'webapp/enums/enums';
import { DataSelectType, ModelName } from 'webapp/enums/enums';
import { equal, getIntervalText } from 'webapp/utils/common';

import type { AdaptabilityValue } from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';

import type Block from 'webapp/models-custom/block';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import DsDataRootTopLevel from 'webapp/models-custom/ds-data/root/top';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type { Props as MetricSetupProps } from 'webapp/models-custom/metric-setup';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	OnChangeProp as MetricSetupOnChangeProp,
	OnChangePropValue as MetricSetupOnChangePropValue,
} from 'webapp/models-custom/metric-setup-base';
import StatusObject, { StatusObjectType } from 'webapp/models-custom/status-object';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type DataSelectRecord from 'webapp/models/data-select';

const DefaultTitle = 'Unknown metric';

export default class DataSelect extends BufferedModel<
	DataSelectRecord,
	RecordData.DataSelect.Serialized
> {
	@service('constants') declare constantsService: Service.Constants;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;

	get id(): string | null {
		return this.model.id;
	}

	get blockId(): number | null {
		return this.getBuffered('blockId');
	}

	set blockId(value: number | null) {
		this.setBuffered('blockId', value);
	}

	get type(): DataSelectType {
		return this.getBuffered('type');
	}

	set type(value: DataSelectType) {
		this.setBuffered('type', value);
	}

	get metricSetupKey(): string | null {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string | null) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupDimensions(): string[] {
		return this.getBuffered('metricSetupDimensions');
	}

	set metricSetupDimensions(value: string[]) {
		this.setBuffered('metricSetupDimensions', value);
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('metricSetupFilters');
	}

	set metricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('metricSetupFilters', value);
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	get compares(): DSSetup.Compare[] {
		return this.getBuffered('compares');
	}

	set compares(value: DSSetup.Compare[]) {
		this.setBuffered('compares', value);
	}

	get overrides(): RecordData.DataSelect.Overrides {
		return this.getBuffered('overrides');
	}

	set overrides(value: RecordData.DataSelect.Overrides) {
		this.setBuffered('overrides', value);
	}

	get updateFrom(): number | null {
		return this.getBuffered('updateFrom');
	}

	set updateFrom(value: number | null) {
		this.setBuffered('updateFrom', value);
	}

	get recordLimit(): number | null {
		return this.getBuffered('recordLimit');
	}

	set recordLimit(value: number | null) {
		this.setBuffered('recordLimit', value);
	}

	get sortDataSelect(): number | null {
		return this.getBuffered('sortDataSelect');
	}

	set sortDataSelect(value: number | null) {
		this.setBuffered('sortDataSelect', value);
	}

	get skipAutoUpdate(): boolean {
		return this.getBuffered('skipAutoUpdate');
	}

	set skipAutoUpdate(value: boolean) {
		this.setBuffered('skipAutoUpdate', value);
	}

	get constValue(): number | null {
		return this.getBuffered('constValue');
	}

	set constValue(value: number | null) {
		this.setBuffered('constValue', value);
	}

	get visualization(): SeriesVisualization | null {
		return this.getBuffered('visualization');
	}

	set visualization(value: SeriesVisualization | null) {
		this.setBuffered('visualization', value);
	}

	get isStacked(): boolean {
		return this.getBuffered('isStacked');
	}

	set isStacked(value: boolean) {
		this.setBuffered('isStacked', value);
	}

	get aggregation(): Aggregation | null {
		return this.getBuffered('aggregation');
	}

	set aggregation(value: Aggregation | null) {
		this.setBuffered('aggregation', value);
	}

	get shownLabels(): ShownLabels {
		return this.getBuffered('shownLabels');
	}

	set shownLabels(value: ShownLabels) {
		this.setBuffered('shownLabels', value);
	}

	get yAxisIndex(): number | null {
		return this.getBuffered('yAxisIndex');
	}

	set yAxisIndex(value: number | null) {
		this.setBuffered('yAxisIndex', value);
	}

	get startValue(): number | null {
		return this.getBuffered('startValue');
	}

	set startValue(value: number | null) {
		this.setBuffered('startValue', value);
	}

	get order(): number | null {
		return this.getBuffered('order');
	}

	set order(value: number | null) {
		this.setBuffered('order', value);
	}

	get anomalySensitivity(): number | null {
		return this.getBuffered('anomalySensitivity');
	}

	set anomalySensitivity(value: number | null) {
		this.setBuffered('anomalySensitivity', value);
	}

	get anomalyTrainingPeriod(): number | null {
		return this.getBuffered('anomalyTrainingPeriod');
	}

	set anomalyTrainingPeriod(value: number | null) {
		this.setBuffered('anomalyTrainingPeriod', value);
	}

	get anomalyAdaptability(): AdaptabilityValue | null {
		return this.getBuffered('anomalyAdaptability') as AdaptabilityValue | null;
	}

	set anomalyAdaptability(value: AdaptabilityValue | null) {
		this.setBuffered('anomalyAdaptability', value);
	}

	// -------- Setup props ---------

	get isPrimaryMain(): boolean {
		// shown in settings as separate metrics
		return this.isPrimary && this.isMain;
	}

	get isPrimary(): boolean {
		// not updated from other ms
		return !this.updateFrom;
	}

	get isMain(): boolean {
		// not compare, goal, avg, trend, etc.
		return this.type === DataSelectType.MAIN;
	}

	get isCompare(): boolean {
		return this.type === DataSelectType.COMPARE;
	}

	get isAverage(): boolean {
		return this.type === DataSelectType.AVERAGE;
	}

	get isTrend(): boolean {
		return this.type === DataSelectType.TREND;
	}

	get isMedian(): boolean {
		return this.type === DataSelectType.MEDIAN;
	}

	get isGoal(): boolean {
		return this.type === DataSelectType.GOAL;
	}

	get isAnomaly(): boolean {
		return this.type === DataSelectType.ANOMALY;
	}

	get isForecast() {
		return this.type === DataSelectType.FORECAST;
	}

	get isForecastOptimistic() {
		return this.type === DataSelectType.FORECAST_OPTIMISTIC;
	}

	get isForecastPessimistic() {
		return this.type === DataSelectType.FORECAST_PESSIMISTIC;
	}

	get isOneOfForecasts(): boolean {
		return this.isForecast || this.isForecastOptimistic || this.isForecastPessimistic;
	}

	get compareType() {
		return this.mmDataSelectsService.getCompareTypeFromDataSelectType(this.type);
	}

	get isAlwaysLine() {
		return (
			this.isAverage || this.isTrend || this.isMedian || this.isGoal || this.isOneOfForecasts
		);
	}

	get canStack() {
		return this.isMain || this.isCompare;
	}

	get unitName(): string | null {
		return this.metricSetup.unitName;
	}

	get isOutputMultipleAttributes(): boolean {
		return this.metricSetup.isOutputMultipleAttributes;
	}

	get isOutputMultipleUnits(): boolean {
		return this.metricSetup.isOutputMultipleUnits;
	}

	get isOutputMultiples(): boolean {
		return this.metricSetup.isOutputMultiples;
	}

	get isOutputUnlimitedMultiples(): boolean {
		return this.metricSetup.isOutputUnlimitedMultiples;
	}

	get isSingleOutput(): boolean {
		return this.metricSetup.isSingleOutput;
	}

	get isGenericMetric(): boolean {
		return !!this.metricSetup.metric?.isGenericBasic;
	}

	get metricName() {
		return this.metricSetup.metric?.name;
	}

	get metricFormat() {
		return this.metricSetup.metric?.format;
	}

	get shownTitle(): string {
		return this.overrides.title || this.data?.metricName || this.metricName || DefaultTitle;
	}

	set shownTitle(title: string) {
		let newTitle: string | undefined = title;

		if (!title || title === DefaultTitle || title === this.metricName) {
			newTitle = undefined;
		}
		this.setOverride('title', newTitle);
	}

	get defaultFormat() {
		return this.metricDefaults.format;
	}

	get format() {
		return this.overrides.format || this.defaultFormat;
	}

	get dataStoryColor() {
		const visualization = this.block?.blockSetup.visualization;
		if (visualization && VisualizationBase.isDataStory(visualization)) {
			const color = visualization?.colorMap[this.id!] as number;
			return color || 0;
		} else {
			return 0;
		}
	}

	get firstCompare() {
		// used in story-element model
		return this.limitations?.compareCMs[0];
	}

	@cached
	get compareIndex() {
		// more info: blockSetup.getCompareIndexOnPrimaryDs
		let compareIndex = -1;

		if (!this.isPrimary) {
			const block = this.block;
			if (block) {
				compareIndex = block.blockSetup.getCompareIndexOnPrimaryDs(this);
			}
		}

		return compareIndex;
	}

	@tracked requireSingleOutput = false;

	@cached
	get metricSetup(): MetricSetup {
		const props: MetricSetupProps = {
			key: this.metricSetupKey,
			spaceAccessId: this.metricSetupSpaceAccessId,
			dimensions: this.metricSetupDimensions,
			filters: this.metricSetupFilters,
		};

		if (this.requireSingleOutput) {
			props.allowAllDimensionValues = false;
			props.allowAllUnits = false;
		}

		const metricSetup = new MetricSetup(props, this.handleMetricSetupUpdate.bind(this));

		return metricSetup;
	}

	get isMissingMetric(): boolean {
		if (this.mmMetricsService.isMetricsRequested) {
			return !!this.metricSetupKey && !this.metricSetup.metric;
		}
		return false;
	}

	get hasNoMetricPermission(): boolean {
		// is there any other option?
		return this.isMissingMetric && !this.currentUserService.isAdmin;
	}

	private _metricLimitations: MetricLimitations | null = null;

	@cached
	get limitations(): MetricLimitations {
		// re-creates limitations on metric, dateRange or compare settings change
		// this is primarily needed for "outside" changes (e.g. pusher updates)
		// and updates in another instance of data-select/blockSetup (e.g. block in preview when designer sidebar is updated)
		// must be careful when updating here, bcos accessing limitations too soon
		// (when something is already updated on limitations, but not on the model yet)
		// will cause limitations to "reset" (by reading from model)

		let cachedLimitations = this._metricLimitations;

		if (cachedLimitations) {
			if (
				!equal(cachedLimitations.metric, this.metricSetup.metricData) ||
				!cachedLimitations.dateRange?.isEqual(this.dateRange) ||
				cachedLimitations.compareCMs.some((compareCM, idx) => {
					// pass the formula through the "serializer" to avoid illogical setups (e.g. both constValue & dateRange)
					const compareFormulaCleaned = DsCompare.createFromFormula(
						this.compares[idx] || null,
						cachedLimitations?.dateRange || undefined,
						this.metricDefaults,
					).formula;
					// don't bust cache if the compares are "invalid" (case of type1 w/o dateRange)
					return compareCM.isValid && !compareCM.isEqual(compareFormulaCleaned);
				})
			) {
				cachedLimitations = null;
			}
		}

		if (!cachedLimitations) {
			cachedLimitations = MetricLimitations.createFromDataSelect(this);

			void cachedLimitations.limitationsLoaded?.then(() => {
				// when creating the limitations model, it checks and resets incorrect granularities, compares etc.
				// (checkValidityForAll called in _setupLimitationsDataAndObservers in constructor)
				// so we need to update the model back from the limitations
				// otherwise we fall into infinite limitations re-creation loop
				// THIS IS A TEMPORARY SOLUTION, we will not persist the change
				// we NEED to implement metric-limitations that can hold invalid state (task https://app.asana.com/0/0/1204813252988166/f)
				if (
					cachedLimitations?.dateRange &&
					!cachedLimitations.dateRange.isEqual(this.dateRange)
				) {
					this.dateRange = cachedLimitations.dateRange.formula;
					this.updateComparesFormulaArrayFromCompareModels(cachedLimitations.compareCMs);
				}
				const compareCMs = cachedLimitations?.compareCMs || [];
				const indexesToRemove: number[] = [];
				compareCMs.forEach((compareCM, idx) => {
					const compare = this.compares[idx];
					if (compare && !compareCM.isEqual(compare)) {
						const formula = compareCM.formula!;
						if (formula) {
							this.compares[idx] = formula;
						} else {
							indexesToRemove.push(idx);
						}
					}
				});
				indexesToRemove.forEach((index, idx) => {
					const indexToRemove = index - idx; // bcos we change array in loop
					this.compares.splice(indexToRemove, 1);
				});
			});

			this._metricLimitations = cachedLimitations;
		}

		return cachedLimitations;
	}

	@cached
	get dateRangeCM() {
		return DateRange.createFromFormula(this.dateRange);
	}

	@cached
	get allowAdvancedSetup() {
		// allows multi dimensions and advanced filtering

		// this does not check for current metricSetup to allow dimensions
		// that we check on the spot because of the possibility there is a different metricSetup there (e.g. master)

		if (!this.block?.allowAdvancedSetup) {
			return false;
		}

		const metric = this.metricSetup.metric;
		if (metric) {
			// in-app
			return metric.allowAdvancedSetup;
		} else {
			// reading from data
			return !!this.dataRoot?.isAdvancedFilteringSupported;
		}
	}

	@cached
	get allowAdvancedFilters() {
		// same as allowAdvancedSetup, but for filtering only (see metric.allowAdvancedFilters)
		if (!this.block?.allowAdvancedSetup) {
			return false;
		}

		const metric = this.metricSetup.metric;
		if (metric) {
			// in-app
			return metric.allowAdvancedFilters;
		} else {
			// reading from data
			return !!this.dataRoot?.allowAdvancedFilters;
		}
	}

	get metricData() {
		return this.metricSetup.metricData;
	}

	get metricSetupWithoutFiltersData() {
		return this.metricSetup.metricSetupWithoutFiltersData;
	}

	get metricSetupData() {
		return this.metricSetup.metricSetupData;
	}

	get serializedMetricSetupData() {
		return this.metricSetup.serializedMetricSetupData;
	}

	updateMetricData(metricData: DSSetup.MetricData | null): void {
		if (metricData) {
			const { key, spaceAccessId } = metricData;
			this.updateKeyAndSpaceAccessId(key, spaceAccessId);
		} else {
			this.updateKeyAndSpaceAccessId('', null);
		}
	}

	updateMetricSetupData(metricSetupData: DSSetup.MetricSetupData | null): void {
		if (metricSetupData) {
			const { key, spaceAccessId, dimensions, filters } = metricSetupData;
			this.updateKeyAndSpaceAccessId(key, spaceAccessId);
			this.updateDimensions(dimensions || []);
			this.updateFilters(filters);
		} else {
			this.updateKeyAndSpaceAccessId('', null);
			this.updateDimensions([]);
			this.updateFilters(null);
		}
	}

	updateDimensions(dimensions: string[]): void {
		const metricSetup = this.metricSetup;
		metricSetup.dimensions = dimensions;
		// a split the server refused (422 on `dimensions`) holds every later save back; new splits retry it
		if (this.errors.dimensions) {
			this.validate(['dimensions']);
		}
		if (!this.allowAdvancedFilters) {
			metricSetup.resetFilters();
		}
	}

	updateKeyAndSpaceAccessId(key: string, spaceAccessId: number | null): void {
		this.metricSetup.setFromMetricData({ key, spaceAccessId });

		this.limitations.changeMetric(this.metricSetup.metricData);

		// reset overrides
		this.setOverride('format', undefined);
		this.setOverride('scale', undefined);

		const metricDefaults = this.metricDefaults;
		const compares = this.limitations.compareCMs;
		compares.forEach((compare) => {
			compare.defaultFormat = metricDefaults.changeFormat;
			compare.defaultFunction = metricDefaults.changeFunction;
			compare.setFormatAndFunctionFromDefaults();
		});
		this.updateComparesFormulaArrayFromCompareModels(compares);
	}

	updateAttributes(attributeNames: string[]): void {
		// FOR LEGACY (NON-ADVANCED) ONLY
		if (equal(this.metricSetup.attributeNamesFromBasicFilters, attributeNames)) {
			return;
		}

		this.metricSetup.setFiltersFromAttributeNames(attributeNames);

		this.checkRecordLimit();
		// this.updateComparesFormulaArrayFromCompareModels();
	}

	updateUnit(unitName: string | null): void {
		if (this.unitName === unitName) {
			return;
		}

		this.metricSetup.unitName = unitName;

		this.checkRecordLimit();
		// this.updateComparesFormulaArrayFromCompareModels();
	}

	updateFilters(filters?: DSSetup.MetricSetupFiltersData | null): void {
		this.metricSetup.setFiltersFromData(filters);
	}

	setOptimalUnit(allowAllUnits = true): Promise<void> {
		if (!this.metricSetup.hasUnits) {
			return Promise.resolve();
		}

		const prevUnit = this.metricSetup.specificSelectedUnit;

		const metricSetupData = this.metricSetupWithoutFiltersData;

		if (!metricSetupData) {
			return Promise.resolve();
		}

		const context = this.block?.isFromDataCalculation ? 'script' : '';

		const prePromise = this.mmMetricsService.getOptimalUnit(
			metricSetupData,
			this.dateRangeCM,
			context,
			allowAllUnits,
		);

		return prePromise
			.then((unit) => {
				// check if user has not changed the metricKey inbetween
				const newMetricSetupData = this.metricSetupWithoutFiltersData;
				if (!equal(metricSetupData, newMetricSetupData)) {
					return;
				}

				if (prevUnit !== this.metricSetup.specificSelectedUnit) {
					return;
				}

				this.updateUnit(unit);
			})
			.catch();
	}

	updateDateRange(dateRange: DateRange, onlyRange = false): void {
		const limitations = this.limitations;
		if (onlyRange) {
			limitations.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
			if (!limitations.isNumber) {
				limitations.selectOptimalValidGranulation();
			}
		} else {
			limitations.updateDateRangeFromFormula(dateRange.formula);
		}
		this.dateRange = limitations.dateRange?.formula || dateRange.formula;
		this.updateComparesFormulaArrayFromCompareModels(limitations.compareCMs);
	}

	removeCompare(compare: DsCompare): void {
		const limitations = this.limitations;
		// set last to "None" instead of removing
		if (limitations.compareCMs.length > 1) {
			limitations.removeCompare(compare);
		} else {
			compare.updateFromFormula(null);
		}

		this.updateComparesFormulaArrayFromCompareModels(limitations.compareCMs);
	}

	changeCompare(oldCompare: DsCompare, newCompare: DsCompare): void {
		if (newCompare.id !== oldCompare.id) {
			// newCompare comes from the dropdown, that can "cache" id values of compares
			// upon updating compare, the options are:
			// A) the existing compare has an id, so we need to use that
			// B) if we are switching from "none" (which should have no id) to something else,
			//    we need to set the ID on it to null
			newCompare.id = oldCompare.id;
		}

		if (newCompare.isNone) {
			this.removeCompare(oldCompare);
			return;
		}

		const limitations = this.limitations;
		oldCompare.updateFromFormula(newCompare.getFormula(), limitations.dateRange!);

		this.updateComparesFormulaArrayFromCompareModels(limitations.compareCMs);
	}

	changeCompareToPrevious(): void {
		const prevCompare = DsCompare.createFromFormula(null);
		prevCompare.updateFromTypeId(DsCompare.customPreviousId, this.dateRangeCM);
		this.changeCompare(this.limitations.compare!, prevCompare);
	}

	updateComparesFormulaArrayFromCompareModels(compares = this.limitations.compareCMs): void {
		const comparesArray: DSSetup.Compare[] = [];
		compares.forEach((compare) => {
			const formula = compare.getFormula();
			if (formula) {
				comparesArray.push(formula);
			}
		});
		// console.log({comparesArray});
		this.compares = comparesArray;
	}

	removeCompares(all = false): void {
		// all:true -> remove all (set first to none)
		// all:false -> remove all except first
		const limitations = this.limitations;
		limitations.compareCMs = limitations.compareCMs.slice(0, 1);
		if (all) {
			limitations.compare?.updateFromFormula(null);
		}
		this.updateComparesFormulaArrayFromCompareModels(limitations.compareCMs);
	}

	getDSMapPlaceholder() {
		const dsId = this.numericId;
		return dsId ? this.block?.getDSMapPlaceholderForDsId(dsId) : null;
	}

	getStoryOrder() {
		// data-story specific (numeric)
		const placeholder = this.getDSMapPlaceholder();
		return placeholder ? +placeholder : null;
	}

	private checkRecordLimit(): void {
		// clean up record limit if we have limited selection (also field is hidden)
		if (!this.isOutputUnlimitedMultiples) {
			this.recordLimit = null;
		}
	}

	private handleMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.metricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.metricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.metricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.metricSetupFilters = value as DSSetup.MetricSetupFiltersData | null;
				break;
		}
	}

	@cached
	get metricDefaults(): {
		format: string;
		changeFormat: string;
		changeFunction: CompareFunction;
	} {
		return this.mmMetricsService.getDefaultsForMetric(this.metricSetup.metric);
	}

	setOverride<K extends keyof RecordData.DataSelect.Overrides>(
		key: K,
		value?: RecordData.DataSelect.Overrides[K],
	): void {
		// bcos of equal check in buffer, we need to
		// first create different object, and then set it
		const newOverrides = Object.assign({}, this.overrides);
		if (typeof value === 'undefined') {
			// removing the key
			delete newOverrides[key];
		} else {
			newOverrides[key] = value;
		}
		this.overrides = newOverrides;
	}

	// -------- Tracking ---------

	getMetricTrackingData() {
		const metricName = this.metricSetup.metric?.name || '',
			datasource = this.metricSetup.sourceType?.key || '';

		return {
			datasource, // string to not lose old segmentations, we added datasources array later
			datasources: datasource ? [datasource] : [],
			metrics: metricName ? [metricName] : [],
		};
	}

	// -------- Data props ---------

	block: Block | null = null; // set in block-setup

	@tracked private _data: DSData.RootTop | null = null;

	@cached
	get data(): DSData.RootTop | null {
		if (this._data) {
			// overwrite
			return this._data;
		}

		const id = this.numericId;

		let data;
		if (id) {
			data = (this.block?.data?.dsData || []).find((item) => item.dsId === id);
		}
		return data || null;
	}

	get hasData(): boolean {
		return !!this.data;
	}

	@cached
	get dataRoot(): DsDataRootTopLevel | null {
		const data = this.data;

		if (data) {
			return this.createDataRoot(data);
		}

		return null;
	}

	@cached
	get outputs(): DsDataOutputTopLevel[] {
		return this.dataRoot?.outputs || [];
	}

	@cached
	get isBlockDataDifferentUnits() {
		return !this.block || this.block.isDataDifferentUnits;
	}

	@cached
	get dateText(): string {
		// without brackets
		let text = '';

		const ds = this.dataRoot;
		if (ds) {
			const startLabelInfo = ds.granulationLabels[0],
				endLabelInfo = ds.granulationLabels[ds.granulationLabels.length - 1],
				startLabel = startLabelInfo?.labelShort || startLabelInfo?.label || '',
				endLabel = endLabelInfo?.labelShort || endLabelInfo?.label || '';
			text = getIntervalText(startLabel, endLabel, false);
		}

		return text;
	}

	get lastSync(): number | null {
		return this.dataRoot?.lastSync || null;
	}

	@cached
	get statusObjects(): StatusObject[] {
		// IMPORTANT! does not look in compares!
		let statusObjects = this.dataRoot?.statusObjects || [];
		if (this.isFilterNotAppliedCleared) {
			statusObjects = statusObjects.filter((so) => !so.isFilterNotApplied);
		}
		const filtersNotApplicableStatusObject = this.filtersNotApplicableStatusObject;
		return filtersNotApplicableStatusObject
			? [...statusObjects, filtersNotApplicableStatusObject]
			: statusObjects;
	}

	get isFilterNotAppliedCleared(): boolean {
		// "OK, remove" cleared the breakdown and filters locally: the server's filter_not_applied is stale until the reload
		return !this.metricSetup.dimensions.length && !this.metricSetup.filtersData;
	}

	@cached
	get hasUnapplicableFilters(): boolean {
		// metric capability, not the block feature: outside boards (goals, my metrics) there is no block to allow it
		const metric = this.metricSetup.metric;
		if (!metric) {
			// not loaded (mobile, datawall, before requestMetrics): no breakdown to check against, the server decides
			return false;
		}
		return !metric.allowAdvancedFilters && !!this.metricSetup.unapplicableFilterItems.length;
	}

	@cached
	private get filtersNotApplicableStatusObject(): StatusObject | null {
		// client-side: pre-built metrics apply only "selected breakdown IS ..." filters
		const dataRoot = this.dataRoot;
		if (
			!dataRoot ||
			!this.hasUnapplicableFilters ||
			dataRoot.statusObjects.some((so) => so.isFilterNotApplied)
		) {
			return null;
		}
		return StatusObject.createFromData({
			type: StatusObjectType.FILTERS_NOT_APPLICABLE,
			level: 'warning',
			title: 'Filter not applied',
			description: "These filters aren't supported for this metric.",
		});
	}

	get isSourceDataset(): boolean {
		return !!this.dataRoot?.isSourceDataset;
	}

	createDataRoot(data: DSData.RootTop) {
		return new DsDataRootTopLevel(this, data);
	}

	setFakeData(data: DSData.RootTop | null) {
		// used in forecasts data-story
		this._data = data;
	}

	// ------ Data calc specifics ------

	get isScriptStatic(): boolean {
		// buffered, to get included in dirty props etc (not a record attribute, so not typed on the buffer)
		return (
			(this._buffer.getBuffered('isScriptStatic') as boolean | undefined) ||
			this.limitations.isOnlyAllTimeEnabled
		);
	}

	set isScriptStatic(value: boolean) {
		this._buffer.setBuffered('isScriptStatic', value);
	}

	// -------- Static stuff ---------

	static override modelName = ModelName.DATA_SELECT; // store record type
}
